import { Suspense, lazy, useContext, useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { REACT_BASE_URL } from '../api/Apis';
import Layout from '../components/layout/Layout';
import BackdropLoading from '../components/loading/BackdropLoading';
import { TextContext } from '../context/TextCotext';
import ContextCat from '../context/contextCategory/contextCat';
import AlarmManagementHistory from '../pages/alarm-management-history/AlarmManagementHistory';
import { CommandLogs } from '../pages/scada/CommandLogs';
import { getLoggedInUser } from '../utils/Utils';
import ContextDevice from '../context/contextAllDevice/contextDevice';
import { KEY_ALARMS, KEY_SCADA, KEY_TRENDS, navbar } from '../utils/constants';
import { getEnvs } from '../utils/environments';
import KanbanBoard from '../pages/kanbanBoard';
import Equipment from '../pages/Equipment';
import Analytics from '../pages/Analytics';
// Lazy loaded components
const AlarmHistory = lazy(() => import('../pages/alarm-history/AlarmHistory'));
const EventHistory = lazy(() => import('../pages/event-history/EventHistory'));
const DeviceInformation = lazy(() =>
  import('../pages/device-information/DeviceInformation'),
);
const Setting = lazy(() => import('../pages/setting/Setting'));
// const General = lazy(() => import('../pages/setting/pages/general/General'));
// const Alarms = lazy(() => import('../pages/setting/pages/alarms/Alarms'));
// const Config = lazy(() => import('../pages/setting/pages/config/Config'));
const AlarmManagement = lazy(() =>
  import('../pages/alarm-management/AlarmManagement'),
);
const Developing = lazy(() => import('../pages/Developing'));
const Page404 = lazy(() => import('../pages/Page404'));

export default function Pages() {
  const navigate = useNavigate();
  // const { setSocketData } = useContext(TextContext);
  const [navbarArray, setNavbarArray] = useState([]);
  const user_role = JSON.parse(localStorage.getItem('user_info'))?.role;
  // const socketIoBaseURLRef = useRef(null);

  useEffect(() => {
    if (!getLoggedInUser()) navigate('/auth/login');
  }, []);

  // useEffect(() => {
  //   const socket = io(`${BASE_URL}/alarms`);
  //   socket.on('alarm', (stringData) => {
  //     const data = JSON.parse(stringData);
  //     const { ip } = data;
  //     setSocketData((prevData) => ({ ...prevData, [ip]: data }));
  //   });

  //   return () => {
  //     socket.off('alarm'); // Unsubscribe from the 'alarm' event
  //     socket.disconnect(); // Disconnect the socket connection
  //   };
  // }, [setSocketData]);

  const {
    setSocketData,
    setEquipmentsError,
    setPlayAlarm,
    setAlarmCount,
    setAlarmHappenSocket,
    setIsErrorModalVisible,
  } = useContext(TextContext);
  const socketIoBaseURLRef = useRef(null);
  const alarmListRef = useRef([]);
  const prevActiveCountRef = useRef(0);

  useEffect(() => {
    socketIoBaseURLRef.current = io(`${REACT_BASE_URL}/alarms`);

    const handler = (stringData) => {
      const data = JSON.parse(stringData);
      const { equipment_id, alarmCategory, color, resolveDate, status } = data;
      // console.log('socket-data', data);
      setEquipmentsError((prevErrors) => {
        const existingErrors = prevErrors[equipment_id] || [];
        let updatedErrors = existingErrors
          .map((error) => {
            if (error.alarmCategory === alarmCategory) {
              return resolveDate ? null : { ...error };
            }
            return error;
          })
          .filter(Boolean);

        if (
          !updatedErrors.find(
            (error) => error.alarmCategory === alarmCategory,
          ) &&
          !resolveDate
        ) {
          updatedErrors.push({ alarmCategory, color, status });
        }

        if (updatedErrors.length === 0) {
          const updatedErrorsState = { ...prevErrors };
          delete updatedErrorsState[equipment_id];
          return updatedErrorsState;
        }

        return {
          ...prevErrors,
          [equipment_id]: updatedErrors,
        };
      });

      const newEvent = data;
      const makeKey = (ev) =>
        `${ev.eventId}__${ev.equipment_id}__${ev.parameterName}`;
      const key = makeKey(newEvent);

      const idx = alarmListRef.current.findIndex((ev) => makeKey(ev) === key);

      if (idx === -1) {
        // if it's a resolved event we DON'T push it (no need)
        if (!newEvent.resolveDate) {
          alarmListRef.current.push(newEvent);
        }
      } else {
        // existing found -> update fields or remove if resolved
        if (newEvent.resolveDate) {
          // remove existing (resolved)
          alarmListRef.current.splice(idx, 1);
        } else {
          // update existing with latest incoming fields (keep other props)
          alarmListRef.current[idx] = {
            ...alarmListRef.current[idx],
            ...newEvent,
          };
        }
      }

      // compute derived structures from the cleaned alarmListRef.current
      const alarmArray = alarmListRef.current;
      const activeAlarms = alarmArray.filter((alarm) => {
        const majority = alarm.majority;
        return majority !== 'EVENT' && alarm.resolveDate == null;
      });

      // determine if we have sound-capable alarms (only when there are active alarms)
      const hasSound =
        activeAlarms.length > 0 &&
        activeAlarms.some((a) => !!(a.sound || a.Sound));

      // only open modal / start sound when we transition from 0 -> >0 (prevents flashes)
      const prev = prevActiveCountRef.current;
      const now = activeAlarms.length;
      if (prev < now && hasSound) {
        setIsErrorModalVisible(true);
        setPlayAlarm(true);
      } else if (now === 0) {
        // all cleared
        setIsErrorModalVisible(false);
        setPlayAlarm(false);
      } else {
        // other cases (e.g. count changed but not from 0) -> update play flag according to presence of any sound
        // setPlayAlarm(hasSound);
      }
      prevActiveCountRef.current = now;

      setAlarmHappenSocket(activeAlarms);
      setAlarmCount(activeAlarms.length);

      const byEquipment = alarmArray.reduce((acc, ev) => {
        const id = ev.equipment_id;
        if (id == null) return acc;
        if (!acc[id]) acc[id] = [];
        acc[id].push(ev);
        return acc;
      }, {});

      const latest = Object.keys(byEquipment).reduce((acc, id) => {
        acc[id] = byEquipment[id][byEquipment[id].length - 1];
        return acc;
      }, {});

      setSocketData({
        raw: { ...alarmArray },
        byEquipment,
        latest,
      });
    };

    socketIoBaseURLRef.current.on('alarm', handler);

    return () => {
      socketIoBaseURLRef.current.off('alarm');
      socketIoBaseURLRef.current.disconnect();
    };
  }, []);

  useEffect(() => {
    handleShowNavbar();
  }, []);

  function makeOptionalScadaItems() {
    const envs = getEnvs() || {};
    const items = [];
    if (String(envs.REACT_APP_SCADA_FEATURE) === 'true') {
      const DiagramComp = lazy(() => import('../pages/scada/Scada'));
      items.push({
        tag: <DiagramComp />,
        name: KEY_SCADA,
        href: '/scada',
        current: false,
        isNavbar: true,
      });
    }
    if (String(envs.REACT_APP_KUHPAYE_SCADA_FEATURE) === 'true') {
      const DiagramComp = lazy(() => import('../pages/scada/Kuhpaye/KuhpayeScada'));
      items.push({
        tag: <DiagramComp />,
        name: KEY_SCADA,
        href: '/scada-kuhpaye',
        current: false,
        isNavbar: true,
      });
    }
    if (String(envs.REACT_APP_SIALK_SCADA_FEATURE) === 'true') {
      const SialkComp = lazy(() => import('../pages/scada/Sialk/SialkScada'));
      items.push({
        tag: <SialkComp />,
        name: KEY_SCADA,
        href: '/scada-sialk',
        current: false,
        isNavbar: true,
      });
    }
    if (String(envs.REACT_APP_KAVIRSUZAN_SCADA_FEATURE) === 'true') {
      const KavirSuzanScada = lazy(() =>
        import('../pages/scada/TwoColumnMainScada/TwoColumnScada'),
      );
      items.push({
        tag: (
          <KavirSuzanScada
            Meter1ID={63}
            Meter2ID={64}
            Relay1ID={135}
            Relay2ID={136}
            Trans1ID={107}
            Trans2ID={108}
            Meter1Params={['ACPL1', 'ACPL2', 'ACPL3', 'APT', 'ARPT']}
            Meter2Params={['ACPL1', 'ACPL2', 'ACPL3', 'APT', 'ARPT']}
            Relay1Params={['ACPL1', 'ACPL2', 'ACPL3', 'CT', 'GF']}
            Relay2Params={['ACPL1', 'ACPL2', 'ACPL3', 'CT', 'GF']}
            Trans1Params={[
              'BA-1',
              'BT',
              'DT',
              'OLA',
              'OLT',
              'PR',
              'TA',
              'TT',
              'WT',
              'WTA',
            ]}
            Trans2Params={[
              'BA-1',
              'BT',
              'DT',
              'OLA',
              'OLT',
              'PR',
              'TA',
              'TT',
              'WT',
              'WTA',
            ]}
            RTU_PLC1ID={105}
            RTU_PLC2ID={106}
            OtherRTU_PLCID={74}
            CB1Name={'CB-Cell-1'}
            CB2Name={'CB-Cell-2'}
            CB3Name={'CB-Cell-3'}
            Earth1Name={'ES-Cell-1'}
            Earth2Name={'ES-Cell-2'}
            Earth3Name={'ES-Cell-3'}
            TransKeys={[
              'BUCHHOLTZ',
              'WINDING TEMPERATURE',
              'OIL LEVEL',
              'THERMOMETER',
              'PRESSURE RELIEF',
              'DOOR',
            ]}
            RelayTable1Header={'KSNM-RELAY-1'}
            RelayTable2Header={'KSNM-RELAY-2'}
            TransTable1Header={'LV1-Transformer'}
            TransTable2Header={'LV2-Transformer'}
            MeterTable1Header={'KSNM-LV METER1'}
            MeterTable2Header={'KSNM-LV METER2'}
          />
        ),
        name: KEY_SCADA,
        href: '/scada-KavirSuzan',
        current: false,
        isNavbar: true,
      });
    }
    if (String(envs.REACT_APP_DERAKHSHANPART_SCADA_FEATURE) === 'true') {
      const DerakhshanPartScada = lazy(() =>
        import('../pages/scada/TwoColumnMainScada/TwoColumnScada'),
      );
      items.push({
        tag: (
          <DerakhshanPartScada
            Meter1ID={61}
            Meter2ID={62}
            Relay1ID={71}
            Relay2ID={72}
            Trans1ID={91}
            Trans2ID={108}
            Meter1Params={['ACPL1', 'ACPL2', 'ACPL3', 'APT', 'ARPT']}
            Meter2Params={['ACPL1', 'ACPL2', 'ACPL3', 'APT', 'ARPT']}
            Relay1Params={['ACPL1', 'ACPL2', 'ACPL3', 'CT', 'GF']}
            Relay2Params={['ACPL1', 'ACPL2', 'ACPL3', 'CT', 'GF']}
            Trans1Params={[
              'BA-1',
              'BT',
              'DT',
              'OLA',
              'OLT',
              'PR',
              'TA',
              'TT',
              'WT',
              'WTA',
            ]}
            Trans2Params={[
              'BA-1',
              'BT',
              'DT',
              'OLA',
              'OLT',
              'PR',
              'TA',
              'TT',
              'WT',
              'WTA',
            ]}
            RTU_PLC1ID={87}
            RTU_PLC2ID={88}
            OtherRTU_PLCID={75}
            CB1Name={'CB-Cell-1'}
            CB2Name={'CB-Cell-2'}
            CB3Name={'CB-Cell-3'}
            Earth1Name={'ES-Cell-1'}
            Earth2Name={'ES-Cell-2'}
            Earth3Name={'ES-Cell-3'}
            TransKeys={[
              'BUCHHOLTZ',
              'WINDING TEMPERATURE',
              'OIL LEVEL',
              'THERMOMETER',
              'PRESSURE RELIEF',
              'DOOR',
            ]}
            RelayTable1Header={'KDP-RELAY1'}
            RelayTable2Header={'KDP-RELAY2'}
            TransTable1Header={'LV1-Transformer'}
            TransTable2Header={'LV2-Transformer'}
            MeterTable1Header={'KDP-LV METER1'}
            MeterTable2Header={'KDP-LV METER2'}
          />
        ),
        name: KEY_SCADA,
        href: '/scada-DerakhshanPart',
        current: false,
        isNavbar: true,
      });
    }

    return items;
  }

  let hasAlarms = true;
  const handleShowNavbar = () => {
    const user = localStorage.getItem('user_info');
    const parse = JSON.parse(user);

    const optionalItems = makeOptionalScadaItems();
    const base = [...navbar];
    const afterIndex = base.findIndex((i) => i.name === KEY_TRENDS);
    const insertPos = afterIndex >= 0 ? afterIndex + 1 : base.length;
    base.splice(insertPos, 0, ...optionalItems);
    const fullNavbar = base;

    if (parse?.role !== 'ADMIN') {
      const access = parse?.access;
      const accessArr = access?.map((str) => str.replace(/_/g, ' '));
      const filteredNavbar = fullNavbar.filter((nav) =>
        accessArr?.includes(nav.name),
      );
      setNavbarArray(filteredNavbar);
      hasAlarms = navbarArray.some((item) => item.name == KEY_ALARMS);
    } else {
      setNavbarArray(fullNavbar);
    }
  };

  return (
    <ContextCat>
      <ContextDevice>
        <Layout>
          <Suspense
            fallback={
              <div>
                <BackdropLoading show={true} />
              </div>
            }
          >
            <Routes>
              {navbarArray?.map((item) => (
                <Route key={item.name} path={item.href} element={item.tag} />
              ))}
              {hasAlarms ? (
                <>
                  <Route path="/alarm-history" element={<AlarmHistory />} />
                  <Route
                    path="/alarm-management-history"
                    element={<AlarmManagementHistory />}
                  />
                  <Route
                    path="/alarm-management"
                    element={<AlarmManagement />}
                  />
                  <Route path="/event-history" element={<EventHistory />} />
                </>
              ) : (
                ''
              )}

              <Route
                path="/device-information"
                element={<DeviceInformation />}
              />
              <Route
                path="/setting/*"
                element={
                  user_role === 'ADMIN' ? <Setting /> : <Navigate to="/" />
                }
              >
                {/* <Route index element={<General />} />
            <Route path="alarms" element={<Alarms />} />
            <Route path="config" element={<Config />} /> */}
              </Route>
              <Route path="/developing" element={<Developing />} />
              <Route path="/kanban-board" element={<KanbanBoard />} />
              <Route path="/equipment" element={<Equipment />} />
              <Route path="/analytics" element={<Analytics />} />

              <Route path="*" element={<Page404 />} />
              <Route path="/scada-logs" element={<CommandLogs />} />
            </Routes>
          </Suspense>
        </Layout>
      </ContextDevice>
    </ContextCat>
  );
}

// import { Routes, Route, useNavigate } from 'react-router-dom';
// import { Suspense, lazy, useContext, useEffect, useRef, useState } from 'react';
// import { getLoggedInUser } from '../utils/Utils';
// import { TextContext } from '../context/TextCotext';
// import { io } from 'socket.io-client';
// import BackdropLoading from '../components/loading/BackdropLoading';
// import {
//   KEY_ALARMS,
//   KEY_ANALYSIS,
//   KEY_DASHBOARD,
//   KEY_DEVICES,
//   KEY_INFORMATION,
//   KEY_LOGS,
//   KEY_MONITORING,
//   KEY_POWER_GENERATION,
//   KEY_TRENDS,
//   KEY_USERS,
// } from '../utils/constants';
// import { BASE_URL } from '../api/constant';

// const AlarmHistory = lazy(() => import('../pages/alarm-history/AlarmHistory'));
// const PowerGeneration = lazy(() =>
//   import('../pages/power-generation/PowerGeneration'),
// );
// const Dashboard = lazy(() => import('../pages/Dashboard/Dashboard'));
// const PlantInformation = lazy(() => import('../pages/PlantInformation'));
// const AlarmEvent = lazy(() => import('../pages/alarm-event/AlarmEvent'));
// const DeviceInformation = lazy(() =>
//   import('../pages/device-information/DeviceInformation'),
// );
// const Monitoring = lazy(() => import('../pages/RealTimeEquipment'));
// const Setting = lazy(() => import('../pages/setting/Setting'));
// const General = lazy(() => import('../pages/setting/pages/general/General'));
// const Alarms = lazy(() => import('../pages/setting/pages/alarms/Alarms'));
// const Users = lazy(() => import('../pages/users/Users'));
// const Logs = lazy(() => import('../pages/logs/Logs'));
// const DeviceManagement = lazy(() =>
//   import('../pages/device-management/DeviceManagement'),
// );
// const AlarmManagement = lazy(() =>
//   import('../pages/alarm-management/AlarmManagement'),
// );
// const Developing = lazy(() => import('../pages/Developing'));
// const Page404 = lazy(() => import('../pages/Page404'));
// const PlantAnalysis = lazy(() => import('../pages/plant-analysis/Page'));
// const TrendPlot = lazy(() => import('../pages/trend-plot/Page'));
// const Layout = lazy(() => import('../components/layout/Layout'));

// export default function Pages() {
//   const navigate = useNavigate();
//   useEffect(() => {
//     if (!getLoggedInUser()) navigate('/auth/login');
//   }, []);

//   const { socketData, setSocketData } = useContext(TextContext);
//   const [navbarArray, setNavbarArray] = useState([]);

//   const socketIoBaseURLRef = useRef(null);

//   let alarmObject = {};
//   useEffect(() => {
//     socketIoBaseURLRef.current = io(`${BASE_URL}/alarms`);
//     socketIoBaseURLRef.current.on('alarm', (stringData) => {
//       const data = JSON.parse(stringData);
//       const { ip } = data;
//       alarmObject[ip] = data;
//       setSocketData(alarmObject);
//     });

//     return () => {
//       socketIoBaseURLRef.current.off('alarm'); // Unsubscribe from the 'alarm' event
//       socketIoBaseURLRef.current.disconnect(); // Disconnect the socket connection
//     };
//   }, []);
//   useEffect(() => {
//     handleShowNavbar();
//   }, []);

//   const handleShowNavbar = () => {
//     const user = localStorage.getItem('user_info');
//     const parse = JSON.parse(user);
//     const navbar = [
//       {
//         tag: <Dashboard />,
//         name: KEY_DASHBOARD,
//         href: '/',
//         current: true,
//       },
//       {
//         tag: <AlarmEvent />,
//         name: KEY_ALARMS,
//         href: '/alarm-event',
//         current: false,
//       },
//       {
//         tag: <PowerGeneration />,
//         name: KEY_POWER_GENERATION,
//         href: '/power-generation',
//         current: false,
//       },
//       {
//         tag: <DeviceManagement />,
//         name: KEY_DEVICES,
//         href: '/device-management',
//         current: false,
//       },
//       {
//         tag: <TrendPlot />,
//         name: KEY_TRENDS,
//         href: '/trend-plot',
//         current: false,
//       },
//       {
//         tag: <PlantAnalysis />,
//         name: KEY_ANALYSIS,

//         href: '/plant-analysis',
//         current: false,
//       },
//       {
//         tag: <PlantInformation />,
//         name: KEY_INFORMATION,

//         href: '/plant-information',
//         current: false,
//       },
//       {
//         tag: <Users />,
//         name: KEY_USERS,
//         href: '/users',
//         current: false,
//       },
//       {
//         tag: <Logs />,
//         name: KEY_LOGS,
//         href: '/logs',
//         current: false,
//       },
//       {
//         tag: <Monitoring />,
//         name: KEY_MONITORING,

//         href: '/monitoring',
//         current: false,
//       },
//     ];
//     if (parse?.role !== 'ADMIN') {
//       const acsses = parse?.access;
//       // const newAccess = acsses?.split(',')
//       const acssesArr = acsses?.map((str) => str.replace(/_/g, ' '));

//       const filteredNavbar = navbar.filter((nav) =>
//         acssesArr?.includes(nav.name),
//       );
//       setNavbarArray(filteredNavbar);
//     } else {
//       setNavbarArray(navbar);
//     }
//   };
//   return (
//     <Layout>
//       <Suspense
//         fallback={
//           <div>
//             <BackdropLoading show={true} />
//           </div>
//         }
//       >
//         <Routes>
//           {/* <Route path="/" element={<PlantProfile />} /> */}
//           {navbarArray?.map((item) => {
//             return (
//               <>
//                 <Route path={item.href} element={item.tag} />
//                 {item.name == KEY_ALARMS ? (
//                   <>
//                     <Route path="/alarm-history" element={<AlarmHistory />} />
//                     {/* <Route
//                       path="/alarm-managment"
//                       element={<AlarmManagment />}
//                     /> */}
//                     <Route
//                       path="/alarm-management"
//                       element={<AlarmManagement />}
//                     />
//                   </>
//                 ) : null}
//               </>
//             );
//           })}

//           <Route path="/device-information" element={<DeviceInformation />} />

//           <Route path="/setting/*" element={<Setting />}>
//             {/* <Route index element={<>test settings layout</>} /> */}
//             <Route index element={<General />} />
//             <Route path="alarms" element={<Alarms />} />
//           </Route>

//           <Route path="/developing" element={<Developing />} />
//           <Route path="*" element={<Page404 />} />
//         </Routes>
//       </Suspense>
//     </Layout>
//   );
// }
